import { useContext, useEffect, useRef, useState } from "react";
import {observer} from "mobx-react";

import classNames from "classnames";
import styles from "./styles/SidebarContainer.module.scss";

import ClickableLink from "../../utilities/ClickableLink";
import FontAwesome from "../../utilities/FontAwesome";
import Notification from "./items/NotificationManager";
import LoginButtons from "./items/LoginButtons";
import {StoreContext} from "../../../stores/StoreLoader";
import dynamic from 'next/dynamic';
import {FetchMachine} from "../../machines/FetchMachine";
import {useMachine} from '@xstate/react';
import SidebarContainer from "./SidebarContainer";
import URI from "urijs";
import SafeRouter from "../../utilities/SafeRouter";
import Button from "../../utilities/Button";

const SidebarComponent = {
    "AdminNav": dynamic(() => import("./pages/SidebarNavPage")),
    "Features": dynamic(() => import("./pages/SidebarFeaturesPage")),
    "Social": dynamic(() => import("./pages/SidebarSocialPage")),
    "Design": dynamic(() => import("./pages/SidebarAppearancePage")),
    "Domain": dynamic(() => import("./pages/SidebarDomainPage")),
    "Navigation": dynamic(() => import("./pages/SidebarPagesPage")),
    "AddSocial": dynamic(() => import("./pages/AddNewSourcePage")),
    "Account": dynamic(() => import("./pages/AccountPage")),
    "ClaimLogin": dynamic(() => import('./claim/ClaimLogin')),
    "VerifyOwnership": dynamic(() => import("./claim/VerifyOwnership")),
    'NoPhone': dynamic(() => import('./claim/NoPhone')),
    'Scheduler': dynamic(() => import('./claim/Scheduler')),
    'VerifyByPhone': dynamic(() => import('./claim/VerifyByPhone')),
    'VerifyByEmail': dynamic(() => import('./claim/VerifyByEmail')),
    "EditSource": dynamic(() => import("./pages/EditSourcePage")),
    "ViewSourceType": dynamic(() => import('./pages/ViewSourceTypePage')),
    "EditLocation": dynamic(() => import('./pages/EditLocationPage')),
    "FixSource": dynamic(() => import('./pages/FixSourcePage')),
    "EditAddress": dynamic(() => import('./pages/EditAddressPage')),
    "ErrorPage": dynamic(() => import("./pages/ErrorPage")),
    "SchoolFeedPost": dynamic(() => import("./pages/SchoolFeedPostPage")),
};

const LastPage = {
    "Features": "AdminNav",
    "Social": "AdminNav",
    "Navigation": "AdminNav",
    "Design": "AdminNav",
    "Domain": "AdminNav",
    "Account": "AdminNav",
    "AddSocial": "Social",
    "AdminNav": null,
    "ClaimLogin": null,
    "EditSource": "Social",
    "ViewSourceType": "Features",
    "EditHero": "Features",
    "EditLocation": "Features",
    "FixSource": "Features",
    'VerifyByPhone': 'VerifyByEmail',
    'NoPhone': 'VerifyByPhone',
    'Scheduler': 'VerifyByPhone',
    "EditAddress": "EditLocation",
};

const DisplayText = {
    "Features": "Features",
    "Social": "Social Feeds",
    "Navigation": "Navigation",
    "Design": "Design",
    "Domain": "Domain",
    "AdminNav": "Admin Menu",
    "AddSocial": "Add Content Feed",
    "ClaimLogin": null,
    "EditSource": "Feed Status Report",
    "ViewSourceType": null,
    "VerifyByEmail": "Back",
    "VerifyByPhone": "Back",
    "EditLocation": "Back"
};

const claimViews = [
    'AllSet', 'ClaimLogin', 'LoginPage', 'VerifyByPhone', 'VerifyOwnership', 'VerifyPhone', 'VerifyByEmail', 'Scheduler', 'NoPhone'
];

const hideBackButtonIn = [
    'AdminNav', 'VerifyByEmail', 'VerifyOwnership', 'ClaimLogin', 'Social', 'Navigation', 'Design', 'Domain', 'Account', "Features", "ErrorPage"
];

const topNavButtonClassName = classNames({
    [styles.topNavButton]: true,
});
const topExitButtonClassName = classNames({
    [styles.topExitButton]: true,
});
const noAccessContainerClassName = classNames({
    [styles.noAccessContainer]: true,
});

let hasFetchedAnalysis = false;

const AdminSidebar = observer(props => {
    const {userStore, interfaceStore, organizationStore} = useContext(StoreContext);

    const [currentComponent, setCurrentComponent] = useState("");
    const [current, send] = useMachine(FetchMachine);
    const containerRef = useRef(null);

    useEffect(() => {
        const currentFullUrlParts = URI(interfaceStore.currentFullUrl);

        if (currentFullUrlParts.hasQuery('spview')) {
            const query = currentFullUrlParts.query(true);
            setCurrentComponent(query.spview);

            if (containerRef?.current) {
                containerRef.current.scrollTop = 0;
            }
        } else if (currentComponent) {
            setCurrentComponent(null);
        }
    }, [interfaceStore.currentFullUrl]);

    useEffect(() => {
        if (!hasFetchedAnalysis) {
            hasFetchedAnalysis = true;
            organizationStore.getContentAnalysis();
        }
    }, []);


    const isClaim = claimViews.includes(currentComponent);

    const ComponentToDisplay = currentComponent ? SidebarComponent[currentComponent] : () => <div/>;
    const BackButtonTarget = LastPage[currentComponent];

    const sidebarHeaderClassName = classNames({
        [styles.sidebarHeader]: true,
        [styles.sidebarHeaderLight]: isClaim,
    });


    return <SidebarContainer ref={containerRef} in={!!currentComponent}>
        <Notification/>

        <div className={sidebarHeaderClassName}>
            {hideBackButtonIn.includes(currentComponent) ||
            <Button className={topNavButtonClassName}
                    onClick={() => SafeRouter.setQuery("spview", BackButtonTarget)}
                    title={'Back'}>
                <FontAwesome ariaHidden={true} prefix={'fas'} name="fa-chevron-left"/>
                <h3>{DisplayText[LastPage[currentComponent]]}</h3>
            </Button>}

        </div>
        {currentComponent &&
        (userStore.isAdmin ||
            isClaim) ?
            <ComponentToDisplay currentSourceLoadingState={current} sendSourceLoadingState={send}/> :
            <div className={noAccessContainerClassName}>
                <p style={{textAlign: 'center'}}>Please login below to access admin panel{!interfaceStore.isSchoolBlocks && <>, or if you
                    are the
                    owner of this organization, <ClickableLink href={'?spview=VerifyOwnership'} replace={true}>claim
                    this site</ClickableLink>.</>}
                </p>
                <LoginButtons/>
            </div>}
    </SidebarContainer>
});

export default AdminSidebar;
